import app from '#app/app'
import { appLayout } from '#app/init_app_layout.svelte.ts'
import { newError } from '#app/lib/error'
import { onlyHeadMetadataRequired } from '#app/lib/metadata/update'
import { addRoutes } from '#app/lib/router'
import { commands, reqres } from '#app/radio'
import { getListingMetadata, getListingPathname, getListingById, getElementPathname, getElementMetadata, assignEntitiesToElements, getListingElementById, type SerializedListingWithTotalElements } from '#listings/lib/listings'
import type { ListingElementId } from '#server/types/element'
import type { ListingId } from '#server/types/listing'
import { mainUser } from '#user/lib/main_user'
import type { SerializedUser } from '#users/lib/users'
import { showUserListings } from '#users/users'
import { getSerializedUser } from '#users/users_data'

export default {
  initialize () {
    addRoutes({
      '/lists/:id/element/:elementId(/)': 'showElement',
      '/lists/:id(/)': 'showListing',
      '/lists(/)': 'showMainUserListings',
    }, controller)
  },
}

interface ListingProps {
  listing: SerializedListingWithTotalElements
  creator: SerializedUser
}

async function showListing (listingId: ListingId) {
  try {
    const [
      { default: ListingLayout },
      listing,
    ] = await Promise.all([
      import('./components/listing_layout.svelte'),
      getListingById(listingId),
    ])
    const { creator: creatorId } = listing
    const creator = await getSerializedUser(creatorId)
    const props: ListingProps = { listing, creator }
    if (!onlyHeadMetadataRequired) appLayout.showChildComponent('main', ListingLayout, { props })
    app.navigate(getListingPathname(listing._id), { metadata: getListingMetadata(listing) })
  } catch (err) {
    commands.execute('show:error', err)
  }
}

async function showElement (listingId: ListingId, elementId: ListingElementId) {
  try {
    const { default: ListingLayout } = await import('./components/listing_layout.svelte')
    const { element } = await getListingElementById(elementId)
    if (!element) throw newError(`could not find element ${elementId}`)

    const listing = await getListingById(element.list)
    if (!listing) throw newError(`could not find listing ${element.list}`)

    const { creator: creatorId } = listing
    const creator = await getSerializedUser(creatorId)

    const props: ListingProps = { listing, creator }
    const [ elementWithEntity ] = await assignEntitiesToElements([ element ])

    appLayout.showChildComponent('main', ListingLayout, { props })
    app.navigate(getElementPathname(listing._id, elementId), {
      metadata: getElementMetadata(listing, elementWithEntity),
    })
  } catch (err) {
    commands.execute('show:error', err)
  }
}

export async function showMainUserListings () {
  if (mainUser) {
    return showUserListings(mainUser.username)
  } else {
    reqres.request('require:loggedIn', 'lists')
  }
}

const controller = {
  showListing,
  showElement,
  showUserListings,
  showMainUserListings,
} as const
